import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FileText, TrendingUp, Shield, Clock, CheckCircle, ArrowRight, Award, Users, Target } from 'lucide-react';

const Home = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 },
  };

  const services = [
    {
      icon: <FileText size={40} />,
      title: 'GST Registration',
      description: 'Complete GST registration assistance with expert guidance for your business.',
    },
    {
      icon: <TrendingUp size={40} />,
      title: 'Income Tax Filing',
      description: 'Professional ITR filing services for individuals and businesses.',
    },
    {
      icon: <Shield size={40} />,
      title: 'Tax Planning',
      description: 'Strategic tax planning to minimize liabilities and maximize savings.',
    },
    {
      icon: <Clock size={40} />,
      title: 'Timely Compliance',
      description: 'Ensure all tax deadlines are met with our reminder and filing services.',
    },
  ];

  const features = [
    'Expert Tax Consultants',
    'Affordable Pricing',
    '24/7 Customer Support',
    'Timely Filing',
    'GST & Income Tax Experts',
    'Transparent Process',
  ];

  const stats = [
    { icon: <Users size={32} />, value: '500+', label: 'Happy Clients' },
    { icon: <FileText size={32} />, value: '2000+', label: 'Returns Filed' },
    { icon: <Award size={32} />, value: '10+', label: 'Years Experience' },
    { icon: <Target size={32} />, value: '100%', label: 'Accuracy' },
  ];

  return (
    <div className="pt-32" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1774099690798-c4fe300374b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMGdsYXNzJTIwZmFjYWRlfGVufDB8fHx8MTc3NTU1MzQxMnww&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2D3C92]/95 via-[#2D3C92]/80 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-2xl">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight" data-testid="hero-title">
                Your Trusted Partner for GST & Income Tax Solutions
              </h1>
              <p className="text-lg sm:text-xl text-slate-200 mb-8 leading-relaxed">
                Professional tax consulting services in Indore. We simplify GST compliance and Income Tax filing for individuals and businesses.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="bg-[#10B981] text-white hover:bg-[#047857] px-8 py-3 rounded-full font-medium transition-all shadow-lg hover:shadow-xl inline-flex items-center gap-2"
                  data-testid="hero-cta-contact"
                >
                  Get Started
                  <ArrowRight size={20} />
                </Link>
                <Link
                  to="/gst-services"
                  className="bg-white/10 backdrop-blur-md text-white border-2 border-white hover:bg-white hover:text-[#2D3C92] px-8 py-3 rounded-full font-medium transition-all"
                  data-testid="hero-cta-services"
                >
                  Our Services
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <div className="text-[#2D3C92] mb-2 flex justify-center">{stat.icon}</div>
                <div className="text-3xl font-bold text-[#2D3C92] mb-1">{stat.value}</div>
                <div className="text-slate-600 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4" data-testid="services-title">
              Our Core Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive tax solutions tailored to meet your business and personal needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all"
                data-testid={`service-card-${index}`}
              >
                <div className="text-[#10B981] mb-4 floating">{service.icon}</div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1758519288905-38b7b00c1023?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHx0YXglMjBmaW5hbmNpYWwlMjBjb25zdWx0aW5nJTIwcHJvZmVzc2lvbmFsJTIwc2hha2luZyUyMGhhbmRzfGVufDB8fHx8MTc3NTU1MzQxMnww&ixlib=rb-4.1.0&q=85"
                alt="Professional Consulting"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-6" data-testid="why-choose-title">
                Why Choose Anku Tax Consultant?
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We are committed to providing exceptional tax consulting services with a focus on accuracy, compliance, and customer satisfaction.
              </p>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                    data-testid={`feature-${index}`}
                  >
                    <CheckCircle className="text-[#10B981] flex-shrink-0" size={24} />
                    <span className="text-slate-700 font-medium">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-20 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #2D3C92 0%, #10B981 100%)',
        }}
        data-testid="cta-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Contact us today for professional tax consulting services. Let us handle your tax compliance while you focus on growing your business.
            </p>
            <Link
              to="/contact"
              className="bg-white text-[#2D3C92] hover:bg-slate-100 px-8 py-4 rounded-full font-bold text-lg inline-flex items-center gap-2 transition-all shadow-xl"
              data-testid="cta-contact-button"
            >
              Contact Us Today
              <ArrowRight size={24} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;